import { useState, useCallback } from 'react';
import { Employee } from '../types';
import { getEmployees, addEmployee, updateEmployee, deleteEmployee } from '../services/storageService';
import { requireCompanyId } from './requireCompanyId';

export function useEmployees(companyId: string | null) {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runAction = useCallback(async <T>(action: (cid: string) => Promise<T>): Promise<T> => {
    setLoading(true);
    setError(null);
    try {
      const cid = requireCompanyId(companyId);
      return await action(cid);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unexpected error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const load = useCallback(async () => {
    const data = await runAction((cid) => getEmployees(cid));
    setEmployees(data);
    return data;
  }, [runAction]);

  const add = useCallback(async (emp: Employee) => {
    const updated = await runAction((cid) => addEmployee(emp, cid));
    setEmployees(updated);
    return updated;
  }, [runAction]);

  const update = useCallback(async (emp: Employee) => {
    const updated = await runAction((cid) => updateEmployee(emp, cid));
    setEmployees(updated);
    return updated;
  }, [runAction]);

  const remove = useCallback(async (id: string) => {
    const updated = await runAction((cid) => deleteEmployee(id, cid));
    setEmployees(updated);
    return updated;
  }, [runAction]);

  return { employees, setEmployees, loading, error, load, add, update, remove };
}
